import { useRef, type KeyboardEvent } from 'react';

import { t } from '../../../lib/i18n';
import { cn } from '../utils';

/** The two views of «Фінанси» — artboard `finViews`. */
export const FINANCE_VIEWS = ['balances', 'deposits'] as const;
export type FinanceView = (typeof FINANCE_VIEWS)[number];

/** `/finance` search that opens «Депозити й режими» (see the route's `validateSearch`). */
export const FINANCE_DEPOSITS_SEARCH = { view: 'deposits' } as const;

/** DOM ids the tab and its panel reference each other by. */
export const financeViewTabId = (view: FinanceView) => `finance-view-tab-${view}`;
export const financeViewPanelId = (view: FinanceView) => `finance-view-panel-${view}`;

interface FinanceViewSwitchProps {
  active: FinanceView;
  onChange: (view: FinanceView) => void;
}

/**
 * «Рахунки й баланси / Депозити й режими» — `Redesign-D-Admin`, «Admin ·
 * Фінанси», the strip under the header.
 *
 * A real `tablist`: only the selected tab is in the tab order, and the arrow
 * keys (plus Home/End) move between the two, as the WAI-ARIA tabs pattern
 * expects. Each tab names the panel it controls, so a screen reader announces
 * the relationship rather than two unrelated buttons.
 */
export function FinanceViewSwitch({ active, onChange }: FinanceViewSwitchProps) {
  const refs = useRef<Partial<Record<FinanceView, HTMLButtonElement | null>>>({});

  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = FINANCE_VIEWS.length - 1;
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    const view = next === null ? undefined : FINANCE_VIEWS[next];
    if (!view) return;
    event.preventDefault();
    onChange(view);
    refs.current[view]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={t('adminFinance.views.aria')}
      className="flex w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px] leading-[normal]"
    >
      {FINANCE_VIEWS.map((view, index) => {
        const isActive = view === active;
        return (
          <button
            key={view}
            ref={(node) => {
              refs.current[view] = node;
            }}
            id={financeViewTabId(view)}
            type="button"
            role="tab"
            aria-selected={isActive}
            // Only the rendered panel exists in the DOM.
            aria-controls={isActive ? financeViewPanelId(view) : undefined}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(view)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              'h-9 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap',
              isActive ? 'bg-cf-brand text-white' : 'text-cf-ink',
            )}
          >
            {t(`adminFinance.views.${view}`)}
          </button>
        );
      })}
    </div>
  );
}
